import { useTranslation } from 'react-i18next'
import type { Answers } from '../funnel/flow'

/**
 * Composed visuals for the value beats, in place of stock illustrations.
 *
 * Every one is built either from the product itself or from the visitor's own
 * answers. None of them invents a statistic — the copy guardrails forbid
 * fabricated proof, and a chart is the easiest place to smuggle one in.
 */
export function ValueVisual({ kind, answers }: { kind: string; answers: Answers }) {
  const { t } = useTranslation()

  // "Typical evening" vs "with a question" — an illustration of the idea, not a
  // measurement of it. Both sides are plainly labelled as examples.
  if (kind === 'compare') {
    return (
      <div className="compare-grid">
        <div className="compare-card dull">
          <span className="compare-tag">{t('visual.compare.beforeTag')}</span>
          <p className="compare-line">{t('visual.compare.beforeQ')}</p>
          <p className="compare-line muted">{t('visual.compare.beforeA')}</p>
        </div>
        <div className="compare-card bright">
          <span className="compare-tag">{t('visual.compare.afterTag')}</span>
          <p className="compare-line">{t('visual.compare.afterQ')}</p>
          <div className="compare-actions" aria-hidden>
            <span className="mini-chip">{t('visual.answer')}</span>
            <span className="mini-chip ghost">{t('visual.pass')}</span>
          </div>
        </div>
      </div>
    )
  }

  // A real question card with the pass control visible — the reassurance beat
  // is about "you can always opt out", so show the actual control.
  if (kind === 'prompt-pass') {
    return (
      <div className="prompt-demo">
        <div className="prompt-card-visual">
          <span className="compare-tag">{t('visual.prompt.tag')}</span>
          <p className="prompt-text">{t('visual.prompt.example')}</p>
        </div>
        <div className="prompt-buttons" aria-hidden>
          <span className="mini-chip solid">{t('visual.answer')}</span>
          <span className="mini-chip ghost highlighted">{t('visual.pass')}</span>
        </div>
        <p className="visual-caption">{t('visual.prompt.caption')}</p>
      </div>
    )
  }

  // Their own settings, drawn back: where they put the depth dial and which
  // topics they took off the table. Personalized and entirely factual.
  if (kind === 'pace-meter') {
    const depth = Number(answers.preferred_depth) || 2
    const boundaries = Array.isArray(answers.topic_boundaries)
      ? answers.topic_boundaries.filter((value) => value !== 'prefer_not_to_say')
      : []
    const depthKeys = ['1', '2', '3', '4'] as const
    return (
      <div className="pace-meter">
        <p className="chart-label">{t('visual.pace.label')}</p>
        <div className="pace-track">
          {depthKeys.map((key) => (
            <span
              key={key}
              className={`pace-step${Number(key) <= depth ? ' filled' : ''}${Number(key) === depth ? ' current' : ''}`}
            >
              {t(`questions.preferred_depth.options.${key}`)}
            </span>
          ))}
        </div>
        {boundaries.length ? (
          <>
            <p className="chart-label">{t('visual.pace.offTable')}</p>
            <div className="chip-row">
              {boundaries.map((value) => (
                <span className="excluded-chip" key={String(value)}>
                  {t(`questions.topic_boundaries.options.${String(value)}`)}
                </span>
              ))}
            </div>
          </>
        ) : null}
      </div>
    )
  }

  return null
}
